// kMimeBodyCore — ONE MIME part's raw text -> the bytes it stands for, and then -> a string.
// Pure: no DOM, no REST, no tenant, no clock. Safe in a browser, in Node and in an Azure Function.
//
// IT IS THE BOTTOM OF THE .eml STACK. kMimeHeaderCore uses it (an RFC 2047 encoded-word is base64 or
// quoted-printable followed by a charset decode — exactly this class's job), kMimeMessageCore uses it
// per leaf. Nothing here knows what a header, a boundary or a message is, which is what keeps it
// testable against byte fixtures with no email in sight.
//
// Plan: X:\Source\MOSE\Documentation\AI\mose-eml-viewer.md §1.1, §4 decisions 5 and 6.
//
// ── MEASURED BEFORE IT WAS WRITTEN (plan §4 decision 6 demands it) ───────────────────────────────
// `Deploy\Measure-MoseMimeCharset.js` — 15 passed / 0 failed, the SAME probe body run in Node
// v22.22.2 and Chromium 151, agreeing on every answer:
//
//   * All 13 real-world labels construct: utf-8, windows-1252, iso-8859-1/15, windows-1251, koi8-r,
//     shift_jis, gb18030, big5, euc-kr, utf-16le, iso-2022-jp, us-ascii.
//   * windows-1252 genuinely decodes cp1252 — 0x93/0x94 come back as curly quotes, not as the C1
//     control characters latin-1 would give. A decoder that accepted the label and answered latin-1
//     would pass a construction check and corrupt every western message containing a quote.
//   * A UTF-8 BOM IS STRIPPED BY THE DECODER. So there is deliberately NO BOM-stripping code here:
//     the plan asked for some, and writing it would have been a guard that can never fire.
//   * An UNKNOWN label THROWS RangeError rather than silently substituting utf-8 — which is the only
//     reason `Malformed = "charset"` can be set truthfully instead of guessed.
//   * ⚠ `iso-8859-1` and `us-ascii` both RESOLVE TO `windows-1252`. That is the WHATWG Encoding
//     Standard's deliberate aliasing, and it is what every mail client already does, because real
//     mail labelled iso-8859-1 is overwhelmingly cp1252. Do not "fix" it.
//
// ── WHY THE BASE64 DECODER IS HAND-ROLLED AND IS **NOT** A FIFTH COPY OF `atob` ──────────────────
// `atob` is available in Node 22 (measured) and in every browser, and the estate already wraps it in
// four places (`kFilePreviewRender.DecodeBytes` and friends). It is the wrong tool HERE, for a
// contract reason rather than a taste one: **`atob` THROWS on whitespace**, and a MIME base64 body is
// line-wrapped every 76 characters BY DEFINITION (RFC 2045 §6.8). It also throws on the missing
// padding that real mail routinely carries. So a caller would have to strip and re-pad first, at
// which point it is no longer the same operation.
// This decoder is TOLERANT — the stance every mail client takes, because an email that is 99.99%
// intact must render — and it COUNTS what it skipped, so the caller can say so rather than being
// unable to tell a clean part from a damaged one.
import { getCoreUtils, nz } from "./kCoreHandler";
import { kCp1252Core } from "./kCp1252Core";

/** Bytes plus how many input characters had to be ignored to get them. `Skipped > 0` means the
 *  part was damaged; the bytes are still the best available reading of it. */
export interface IkMimeBytes {
    /** Present only for strict base64 probes; tolerant MIME decoding keeps its original shape. */
    Valid?: boolean;
    Bytes: Uint8Array;
    Skipped: number;
}

/** One decoded part. `Malformed` is a CODE, never a sentence — this class has no language and the
 *  products on it ship in eleven. */
export interface IkMimeDecoded {
    Bytes: Uint8Array;
    Text: string;
    Malformed: string;
}

/** One step of the quoted-printable walk. `Byte < 0` means "emit nothing" (a soft line break). */
interface IkQpStep {
    Byte: number;
    Advance: number;
    Skipped: number;
}

export class kMimeBodyCore {

    /**
     * base64 alphabet -> value, indexed by charCode; -1 means "not a base64 character".
     *
     * ⚠ THE BUILDER IS DECLARED ABOVE THE FIELD THAT CALLS IT, AND THAT ORDER IS LOAD-BEARING.
     * These are arrow-function CLASS FIELDS, which initialise top to bottom at class-evaluation
     * time — a table declared first would run `_BuildB64Table` while it is still `undefined` and
     * throw before any consumer existed. The estate has already paid for the class-field-vs-
     * prototype distinction once (a `.prototype.X.call()` that was `undefined.call`); this is the
     * same fact wearing a different hat.
     *
     * DOCUMENTED EXCEPTION: no error-mode guard and no try/catch, and here the guard would be an
     * outright DEFECT. This runs ONCE, at class-evaluation time, to initialise a static field — so a
     * guard that returned an empty table because the gate happened to be up at import would leave
     * every base64 decode in the process answering "not a base64 character" to every character,
     * FOR THE LIFETIME OF THE WORKER, long after the gate was reset. It fills a 128-entry array from
     * a literal and cannot fail.
     */
    private static _BuildB64Table = (): Int8Array => {
        const Table: Int8Array = new Int8Array(128).fill(-1);
        const Alphabet: string = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
        for (let i: number = 0; i < Alphabet.length; i++) { Table[Alphabet.charCodeAt(i)] = i; }
        return Table;
    };

    private static _B64: Int8Array = kMimeBodyCore._BuildB64Table();

    /**
     * An empty result, so every guarded return in this file is the same shape.
     *
     * DOCUMENTED EXCEPTION: no error-mode guard and no try/catch. This IS the value the guards
     * return — `if (KErrorMode) { return kMimeBodyCore._NoBytes(); }` — so putting a guard inside it
     * asks the gate about itself, and a try/catch would wrap an object literal.
     */
    private static _NoBytes = (): IkMimeBytes => {
        return { Bytes: new Uint8Array(0), Skipped: 0 };
    };

    /** An empty decode, ditto. DOCUMENTED EXCEPTION: the `_NoBytes` reason, verbatim. */
    private static _NoText = (): IkMimeDecoded => {
        return { Bytes: new Uint8Array(0), Text: "", Malformed: "" };
    };

    /**
     * CR, LF, TAB, SPACE and `=` padding are STRUCTURE in a base64 body, not damage.
     *
     * DOCUMENTED EXCEPTION: one comparison of a number against five constants. Nothing to guard,
     * nothing that can throw — and answering `false` in error mode would reclassify every newline in
     * an attachment as corruption and inflate the `Skipped` count the caller surfaces.
     */
    private static _IsB64Skippable = (Code: number): boolean => {
        return Code === 13 || Code === 10 || Code === 9 || Code === 32 || Code === 61;
    };

    /**
     * The 6-bit value of one character, or -1.
     *
     * DOCUMENTED EXCEPTION: a bounds test and an array index, called once PER CHARACTER of every
     * base64 part — so a try/catch here is a try/catch per byte of every attachment in the mailbox.
     */
    private static _B64Value = (Code: number): number => {
        if (Code >= 128) { return -1; }
        return kMimeBodyCore._B64[Code];
    };

    /**
     * base64 text -> bytes, TOLERANTLY.
     *
     * Anything that is not base64 and not structure is skipped and COUNTED, because "there were
     * three characters here that are not base64" is a fact the caller may want to surface, and a
     * silent skip makes a corrupted attachment indistinguishable from a clean one.
     *
     * ⚠ IT CANNOT THROW ON BAD INPUT, and that is deliberate. A malformed part is an EXPECTED
     * condition in real mail (law 1: expected conditions are CORRECTED, not reported) — reporting it
     * would latch KErrorMode and then silently suppress every guarded continuation downstream,
     * including the render of the ninety-nine parts that were fine.
     */
    public static Base64ToBytes = (Text: string, Strict: boolean = false): IkMimeBytes => {
        if (getCoreUtils().KErrorMode) { return kMimeBodyCore._NoBytes(); }
        try {
            const Raw: string = nz(Text);
            if (Strict === true) {
                let PlainLength: number = Raw.length;
                const PaddingAt: number = Raw.indexOf("=");
                if (PaddingAt >= 0) { PlainLength = PaddingAt; }
                if (/^[A-Za-z0-9+/]*={0,2}$/.test(Raw) === false || PlainLength % 4 === 1
                    || (PaddingAt >= 0 && Raw.length % 4 !== 0)) {
                    return { Bytes: new Uint8Array(0), Skipped: 0, Valid: false };
                }
                if (Raw === "") { return { Bytes: new Uint8Array(0), Skipped: 0, Valid: true }; }
            }
            if (Raw === "") { return kMimeBodyCore._NoBytes(); }
            const Out: Uint8Array = new Uint8Array(Math.ceil(Raw.length * 3 / 4) + 3);
            let Written: number = 0;
            let Skipped: number = 0;
            let Acc: number = 0;
            let Bits: number = 0;
            for (let i: number = 0; i < Raw.length; i++) {
                const Code: number = Raw.charCodeAt(i);
                if (kMimeBodyCore._IsB64Skippable(Code)) { continue; }
                const Val: number = kMimeBodyCore._B64Value(Code);
                if (Val < 0) { Skipped++; continue; }
                Acc = (Acc << 6) | Val;
                Bits += 6;
                if (Bits >= 8) {
                    Bits -= 8;
                    Out[Written] = (Acc >> Bits) & 0xFF;
                    Written++;
                }
            }
            if (Strict === true) { return { Bytes: Out.subarray(0, Written), Skipped: Skipped, Valid: true }; }
            return { Bytes: Out.subarray(0, Written), Skipped: Skipped };
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kMimeBodyCore.Base64ToBytes");
            return kMimeBodyCore._NoBytes();
        }
    };

    /**
     * Two hex digits. `parseInt` alone would accept "4Z" as 4 and swallow the Z.
     *
     * ⚠ PUBLIC BECAUSE kMimeWordCore HAD A BYTE-IDENTICAL PRIVATE COPY (removed 2026-08-18). Both
     * classes validate the SAME two-character escape — `=XX` in quoted-printable here, `%XX` in an
     * RFC 2231 extended parameter there — so a divergence would mean one of the two encodings
     * silently accepting an escape the other rejects. kMimeWordCore already imports this class, so
     * exposing the predicate costs no new dependency.
     *
     * DOCUMENTED EXCEPTION: no error-mode guard and no try/catch. It reads two character codes and
     * compares them against three ranges — there is no call, no allocation and nothing that can
     * throw. A guard would additionally make it answer `false` in error mode, i.e. silently reclassify
     * every valid escape as invalid on a page that is already failing, which corrupts the decode
     * rather than stopping it.
     */
    public static IsHexPair = (Pair: string): boolean => {
        if (Pair.length !== 2) { return false; }
        for (let i: number = 0; i < 2; i++) {
            const C: number = Pair.charCodeAt(i);
            if (C >= 48 && C <= 57) { continue; }
            if (C >= 65 && C <= 70) { continue; }
            if (C >= 97 && C <= 102) { continue; }
            return false;
        }
        return true;
    };

    /**
     * One `=` escape in a quoted-printable stream.
     *
     * ⚠ THE SOFT LINE BREAK IS `=` AT END OF LINE AND IT DELETES THE NEWLINE — it is not a
     * character. Getting this wrong sprinkles a stray `=` every 76 characters through the whole
     * body, which reads on screen as a font or encoding problem rather than as a decode bug, and is
     * therefore hunted for in the wrong layer (plan §4 decision 5).
     *
     * A lone `=` that is neither a soft break nor a valid hex pair is KEPT as a literal and counted.
     * Real senders emit these; dropping one silently would rewrite the sender's text, which on an
     * evidence surface is worse than leaving it ugly.
     *
     * DOCUMENTED EXCEPTION: no error-mode guard and no try/catch. It reads three characters out of a
     * string it is handed and returns a step description; `charAt`/`substring` past the end answer
     * `""` rather than throwing, which is what makes the end-of-input case ordinary rather than
     * exceptional. Its caller, `QuotedPrintableToBytes`, is guarded and reports.
     */
    private static _QpEscape = (Raw: string, At: number): IkQpStep => {
        if (Raw.charAt(At + 1) === "\r" && Raw.charAt(At + 2) === "\n") {
            return { Byte: -1, Advance: 3, Skipped: 0 };
        }
        if (Raw.charAt(At + 1) === "\n") { return { Byte: -1, Advance: 2, Skipped: 0 }; }
        const Hex: string = Raw.substring(At + 1, At + 3);
        if (kMimeBodyCore.IsHexPair(Hex)) {
            return { Byte: parseInt(Hex, 16), Advance: 3, Skipped: 0 };
        }
        return { Byte: 61, Advance: 1, Skipped: 1 };
    };

    /**
     * quoted-printable text -> bytes.
     *
     * @param UnderscoreIsSpace RFC 2047's **Q** encoding maps `_` to a space; a quoted-printable
     *        BODY does not, and `_` is an ordinary character there. That is the single difference
     *        between the two, so it is a defaulted parameter rather than a second near-identical
     *        method — rule 2 applied to code being written for the first time. Default `false` = the
     *        BODY rule, which is what every caller outside kMimeHeaderCore wants.
     */
    public static QuotedPrintableToBytes = (Text: string,
        UnderscoreIsSpace: boolean = false): IkMimeBytes => {
        if (getCoreUtils().KErrorMode) { return kMimeBodyCore._NoBytes(); }
        try {
            const Raw: string = nz(Text);
            if (Raw === "") { return kMimeBodyCore._NoBytes(); }
            const Out: Uint8Array = new Uint8Array(Raw.length);
            let Written: number = 0;
            let Skipped: number = 0;
            let i: number = 0;
            while (i < Raw.length) {
                const Ch: string = Raw.charAt(i);
                if (Ch === "=") {
                    const Step: IkQpStep = kMimeBodyCore._QpEscape(Raw, i);
                    if (Step.Byte >= 0) { Out[Written] = Step.Byte; Written++; }
                    Skipped += Step.Skipped;
                    i += Step.Advance;
                    continue;
                }
                if (Ch === "_" && UnderscoreIsSpace === true) { Out[Written] = 32; }
                else { Out[Written] = Raw.charCodeAt(i) & 0xFF; }
                Written++;
                i++;
            }
            return { Bytes: Out.subarray(0, Written), Skipped: Skipped };
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kMimeBodyCore.QuotedPrintableToBytes");
            return kMimeBodyCore._NoBytes();
        }
    };

    /**
     * Bytes + a charset LABEL -> a string.
     *
     * An unknown or absent label falls back to UTF-8 and reports `Malformed = "charset"` in the
     * result, so the caller can badge the message rather than shipping mojibake as fact. That is the
     * plan's stated fallback (§4 decision 6) and it is reachable ONLY because an unknown label
     * throws — measured, not assumed.
     *
     * ⚠ `fatal: true` IS DELIBERATELY NOT USED, and it WAS measured as available (it throws
     * TypeError on bad bytes). It would let this class flag individual damaged BYTES — but real mail
     * is full of one stray byte in an otherwise perfect message, so flagging those would put an
     * "incomplete" badge on messages that render fine, and a badge that cries wolf stops meaning
     * anything. Only a charset we could not honour AT ALL is reported, because that one really can
     * corrupt the whole part.
     */
    public static BytesToText = (Bytes: Uint8Array, Charset: string): IkMimeDecoded => {
        if (getCoreUtils().KErrorMode) { return kMimeBodyCore._NoText(); }
        try {
            const Label: string = nz(Charset).trim().toLowerCase();
            let Wanted: string = Label;
            if (Wanted === "") { Wanted = "utf-8"; }
            // InstanceType<typeof TextDecoder> — see _Utf8 below for why the plain type name is
            // not usable in a server-safe file.
            let Decoder: InstanceType<typeof TextDecoder> = kMimeBodyCore._Utf8();
            let Malformed: string = "";
            try {
                Decoder = new TextDecoder(Wanted);
            } catch (Unknown) {
                // ERROR-SUPPRESSED-JUSTIFIED: an unrecognised charset label is an EXPECTED condition
                // in twenty-year-old mail, not a fault in this code. Reporting it would latch
                // KErrorMode and abort the render of every other part. It is CORRECTED (fall back to
                // UTF-8) and CARRIED OUT in the answer as Malformed — law 1's fate 1, stated.
                Decoder = kMimeBodyCore._Utf8();
                Malformed = "charset";
            }
            // windows-1252 (also every label WHATWG aliases to it: iso-8859-1, us-ascii, ...) is decoded by our own
            // table: Node v22.20.0 on Linux decodes it as ISO-8859-1 under the right label (measured 2026-10-17).
            if (Decoder.encoding === "windows-1252") {
                return { Bytes: Bytes, Text: kCp1252Core.Decode(Bytes), Malformed: Malformed };
            }
            return { Bytes: Bytes, Text: Decoder.decode(Bytes), Malformed: Malformed };
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kMimeBodyCore.BytesToText");
            return kMimeBodyCore._NoText();
        }
    };

    /**
     * The one decoder that is guaranteed to construct, so the fallback path cannot itself fail.
     *
     * DOCUMENTED EXCEPTION: no error-mode guard and no try/catch — the guard would defeat the whole
     * point of the method. It exists to be the LAST resort when a sender's charset label did not
     * resolve; a version that could itself decline (empty in error mode, or a catch of its own) is
     * exactly the "the fallback failed too" hole it was written to close. `TextDecoder("utf-8")` is
     * mandatory in every runtime this ships to.
     */
    // The return type is InstanceType<typeof TextDecoder>, not TextDecoder. Under @types/node
    // WITHOUT the DOM lib - which is what a server-safe tree must compile with, since "DOM" would
    // let document and window typecheck in an Azure Function - TextDecoder is declared only as a
    // `declare var`, i.e. a VALUE with no matching type name, and `: TextDecoder` is TS2749. This
    // form reads the instance type off the value and is identical under lib.dom, where TextDecoder
    // is both. Found 2026-08-21 by the first consumer to compile the core tree with lib:["ES2020"].
    private static _Utf8 = (): InstanceType<typeof TextDecoder> => {
        return new TextDecoder("utf-8");
    };

    /**
     * Raw part text + its `Content-Transfer-Encoding` -> the BYTES, with no charset step.
     *
     * ⚠ IT IS SEPARATE FROM `Decode` BECAUSE A BINARY PART MUST NOT BE TURNED INTO TEXT. `Decode`
     * finishes by running `BytesToText` over the result, which for a 7 MB PDF or a JPEG allocates a
     * multi-megabyte string of mojibake that the caller then throws away — and on a charset the part
     * never meant to declare. A caller that wants a file's bytes (to hand to a download, a hash or a
     * writer) wants exactly this and nothing after it.
     *
     * ⚠ AND IT IS AN EXTRACTION, NOT A SECOND IMPLEMENTATION. `Decode` now calls it, so the dispatch
     * over base64 / quoted-printable / everything-else exists ONCE. `ByteLength`'s docblock already
     * states the rule for the sibling case: a copy of this dispatch would drift the first time a new
     * transfer encoding, or a change to what counts as skippable, moved only one of them.
     *
     * An UNKNOWN transfer encoding is treated as `8bit` (the bytes as written) rather than refused.
     * That is the tolerant direction and it matches every mail client: a part labelled with a
     * private encoding is far more often plain text with an odd label than it is genuinely encoded.
     */
    public static DecodeToBytes = (Raw: string, TransferEncoding: string): IkMimeBytes => {
        if (getCoreUtils().KErrorMode) { return kMimeBodyCore._NoBytes(); }
        try {
            const Encoding: string = nz(TransferEncoding).trim().toLowerCase();
            if (Encoding === "base64") { return kMimeBodyCore.Base64ToBytes(Raw); }
            if (Encoding === "quoted-printable") {
                return kMimeBodyCore.QuotedPrintableToBytes(Raw, false);
            }
            return { Bytes: kMimeBodyCore._Latin1Bytes(Raw), Skipped: 0 };
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kMimeBodyCore.DecodeToBytes");
            return kMimeBodyCore._NoBytes();
        }
    };

    /**
     * The whole job for one part: raw part text + its `Content-Transfer-Encoding` + its charset ->
     * bytes and text.
     *
     * An UNKNOWN transfer encoding is treated as `8bit` (the bytes as written) rather than refused.
     * That is the tolerant direction and it matches every mail client: a part labelled with a
     * private encoding is far more often plain text with an odd label than it is genuinely encoded.
     */
    public static Decode = (Raw: string, TransferEncoding: string, Charset: string): IkMimeDecoded => {
        if (getCoreUtils().KErrorMode) { return kMimeBodyCore._NoText(); }
        try {
            const Encoding: string = nz(TransferEncoding).trim().toLowerCase();
            const Raw8: IkMimeBytes = kMimeBodyCore.DecodeToBytes(Raw, Encoding);
            // ⚠ THE NAME OF THE MALFORMED CODE IS THE ENCODING THAT PRODUCED IT, so it is decided
            // here rather than inside the extraction: `DecodeToBytes` reports HOW MANY characters it
            // had to skip and stays silent about what that means, which is what lets a caller who
            // only wants bytes ignore it.
            let Malformed: string = "";
            if (Raw8.Skipped > 0 && Encoding === "base64") { Malformed = "base64"; }
            if (Raw8.Skipped > 0 && Encoding === "quoted-printable") {
                Malformed = "quoted-printable";
            }
            const Decoded: IkMimeDecoded = kMimeBodyCore.BytesToText(Raw8.Bytes, Charset);
            // The charset problem outranks the transfer-encoding one: it can corrupt the WHOLE part,
            // where a few skipped base64 characters corrupt a few bytes.
            let Final: string = Malformed;
            if (Decoded.Malformed !== "") { Final = Decoded.Malformed; }
            return { Bytes: Raw8.Bytes, Text: Decoded.Text, Malformed: Final };
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kMimeBodyCore.Decode");
            return kMimeBodyCore._NoText();
        }
    };

    /**
     * How many BYTES a part decodes to, WITHOUT allocating them.
     *
     * ⚠ THE NUMBER A VIEWER SHOWS MUST BE THE DECODED LENGTH. base64 inflates by about a third, so
     * printing the raw length tells an operator a 1 MB attachment is 1.37 MB — and MOSE already
     * shows a decoded size on the wire elsewhere, so the two would visibly disagree about the same
     * file.
     *
     * ⚠ AND IT MUST NOT BE A SECOND base64 IMPLEMENTATION. It counts with the SAME `_IsB64Skippable`
     * and `_B64Value` predicates the decoder uses, so a change to what counts as a skippable
     * character moves both or neither. A hand-rolled `raw.length * 3 / 4` would be a copy that drifts
     * the first time line wrapping or padding changes.
     *
     * Only base64 gets the shortcut, because only base64 has a closed form. quoted-printable has no
     * length without walking the escapes, so it is decoded — which is affordable because a QP part is
     * text by definition and text does not reach the sizes an attachment does.
     */
    public static ByteLength = (Raw: string, TransferEncoding: string): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            const Encoding: string = nz(TransferEncoding).trim().toLowerCase();
            if (Encoding !== "base64") {
                return kMimeBodyCore.Decode(Raw, Encoding, "").Bytes.length;
            }
            const Text: string = nz(Raw);
            let Chars: number = 0;
            for (let i: number = 0; i < Text.length; i++) {
                const Code: number = Text.charCodeAt(i);
                if (kMimeBodyCore._IsB64Skippable(Code)) { continue; }
                if (kMimeBodyCore._B64Value(Code) < 0) { continue; }
                Chars++;
            }
            return Math.floor(Chars * 6 / 8);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kMimeBodyCore.ByteLength");
            return 0;
        }
    };

    /**
     * A JS string whose characters are already bytes (a `7bit`/`8bit`/`binary` part read as text)
     * -> those bytes.
     *
     * ⚠ MASKING TO 0xFF IS THE POINT. The caller has read the file as a string, so a byte 0xE5
     * arrived as the CHARACTER U+00E5. Re-encoding it as UTF-8 here would turn one byte into two and
     * the charset decode would then see gibberish — a double-decode producing the classic `Ã¥`
     * mojibake, several layers away from its cause.
     *
     * DOCUMENTED EXCEPTION: no error-mode guard and no try/catch. A loop over a string writing masked
     * character codes into an array it allocated itself — no call, no external state. Answering an
     * EMPTY array in error mode would present a `7bit` part as a zero-byte file, which downstream is
     * indistinguishable from the sender having attached nothing.
     */
    private static _Latin1Bytes = (Raw: string): Uint8Array => {
        const Text: string = nz(Raw);
        const Out: Uint8Array = new Uint8Array(Text.length);
        for (let i: number = 0; i < Text.length; i++) { Out[i] = Text.charCodeAt(i) & 0xFF; }
        return Out;
    };
}
